import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Youtube, Facebook } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/lib/images";
import { api, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PORTRAITS = [
  { src: IMAGES.candid, label: "Candid", tall: true },
  { src: IMAGES.portraitAlt, label: "Portrait" },
  { src: IMAGES.leaders, label: "Among Leaders" },
];

export const Gallery = () => {
  const [uploads, setUploads] = useState([]);
  const { social } = useSettings();

  const ytEmbed = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
      if (u.pathname.startsWith("/live/")) return `https://www.youtube.com/embed/${u.pathname.split("/live/")[1]}`;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    } catch (e) {}
    return null;
  };

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => {
        const tiles = [];
        r.data.forEach((item) =>
          (item.media || []).forEach((m) =>
            tiles.push({ ...m, caption: item.caption || item.title || "" })
          )
        );
        setUploads(tiles);
      })
      .catch(() => {});
  }, []);

  return (
    <section
    id="gallery"
    data-testid="gallery-section"
    className="relative py-24 sm:py-32 bg-parchment dark:bg-ink"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <Overline>Media & Gallery</Overline>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            A word, and a witness.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hear from Charles directly, and see the ministry in moments captured
            along the way.
          </p>
        </Reveal>
      </div>

      {(social?.youtube || social?.facebook) && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-8">
            {ytEmbed(social?.youtube) ? (
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-gold/30">
                <iframe
                  data-testid="youtube-embed"
                  src={ytEmbed(social.youtube)}
                  title="YouTube"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="h-full min-h-[220px] rounded-2xl border border-gold/20 bg-card flex items-center justify-center text-muted-foreground text-sm p-6 text-center">
                Paste a YouTube video / live link in Admin → Site to publish it here.
              </div>
            )}
          </Reveal>
          <div className="lg:col-span-4 flex flex-col gap-4 justify-center">
            {social?.youtube && (
              <a data-testid="watch-youtube" href={social.youtube} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl border border-[#FF4D4D]/40 bg-[#FF0000]/10 hover:bg-[#FF0000]/20 transition-colors">
                <Youtube className="text-[#FF6B6B]" size={24} /><span className="font-semibold">Watch on YouTube</span>
              </a>
            )}
            {social?.facebook && (
              <a data-testid="watch-facebook" href={social.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl border border-[#4D94FF]/40 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition-colors">
                <Facebook className="text-[#6BA8FF]" size={24} /><span className="font-semibold">Facebook Live &amp; Videos</span>
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-5">
          <div className="relative rounded-3xl overflow-hidden border border-gold/30 bg-ink">
            <video
              data-testid="media-video"
              controls
              playsInline
              preload="metadata"
              poster={IMAGES.portrait}
              className="w-full aspect-[3/4] object-cover bg-ink"
            >
              <source src={IMAGES.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="pointer-events-none absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/80 bg-ink/50 px-3 py-1 rounded-full backdrop-blur-sm">
              A Word from Charles
            </div>
          </div>
        </Reveal>

        <div className="lg:col-span-7 grid grid-cols-2 gap-6">
          {PORTRAITS.map((p, i) => (
            <Reveal
              key={p.label}
              delay={i * 0.1}
              className={p.tall ? "row-span-2" : ""}
            >
              <motion.div
                data-testid={`gallery-portrait-${i}`}
                whileHover={{ y: -6 }}
                className={`group relative h-full rounded-2xl overflow-hidden border border-border ${
                  p.tall ? "min-h-[320px]" : "min-h-[150px]"
                }`}
              >
                <img
                  src={p.src}
                  alt={`Charles McKuhn — ${p.label}`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 font-serif text-lg font-semibold text-parchment">
                  {p.label}
                </span>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {uploads.map((m, i) => (
            <Reveal key={i} delay={(i % 4) * 0.08}>
              <div
                data-testid={`gallery-upload-${i}`}
                className="group relative aspect-square rounded-2xl overflow-hidden border border-border"
              >
                {m.type === "video" ? (
                  <video
                    src={mediaUrl(m.url)}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover bg-ink"
                  />
                ) : (
                  <img
                    src={mediaUrl(m.url)}
                    alt={m.caption || "Ministry moment"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}
                {m.caption && (
                  <>
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                    <span className="pointer-events-none absolute bottom-3 left-3 right-3 text-sm font-medium text-parchment">
                      {m.caption}
                    </span>
                  </>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  </section>
  );
};
